import type { StageDwellSegment } from '@/services/loans'
import type { ClosingChecklist } from '@/services/journal'
import type {
  DealTeamMember,
  JournalEntry,
  NextActionRow,
  ObligationRow,
  PartyNode,
} from './workspace/types'

export type DealTimelineItem = {
  id: string
  at: string
  title: string
  detail: string | null
  kind: 'stage' | 'note' | 'activity'
  actor: string | null
}

export type JournalTaskRow = {
  id: string
  checklistId: string | null
  itemId: string | null
  label: string
  owner: string | null
  dueDate: string | null
  status: string
  state: 'open' | 'overdue' | 'done'
  source: 'checklist' | 'condition' | 'next_action'
}

export type JournalCollaborator = {
  id: string
  name: string
  role: string
  relationship: 'internal' | 'external'
}

const DONE_STATUSES = new Set(['received', 'waived', 'n/a', 'complete', 'completed', 'done', 'resolved'])

function timestamp(value: string): number {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** Values a record carries in place of a person. The loan's assignee
 *  defaults to "Unassigned", and listing it as a collaborator put a state on
 *  the page as though it were somebody on the deal. */
const PLACEHOLDER_NAMES = new Set(['—', '-', 'unassigned', 'unassigned/originations', 'not assigned', 'tbd', 'n/a', 'none', 'unknown'])

function usableName(value: string | null | undefined): value is string {
  return Boolean(value && value.trim() && !PLACEHOLDER_NAMES.has(value.trim().toLowerCase()))
}

export function buildDealTimeline(
  segments: readonly StageDwellSegment[],
  journal: readonly JournalEntry[],
): DealTimelineItem[] {
  const stageItems: DealTimelineItem[] = segments
    .filter((segment) => Boolean(segment.enteredAt))
    .map((segment, index) => ({
      id: `stage-${index}-${segment.stage}-${segment.enteredAt}`,
      at: segment.enteredAt,
      title: `Entered ${segment.stage}`,
      detail: segment.current
        ? `${segment.days} day${segment.days === 1 ? '' : 's'} in current stage`
        : `${segment.days} day${segment.days === 1 ? '' : 's'} in stage`,
      kind: 'stage',
      actor: null,
    }))

  const journalItems: DealTimelineItem[] = journal.map((entry) => ({
    id: `journal-${entry.id}`,
    at: entry.at,
    title: entry.title,
    detail: entry.summary,
    kind: entry.kind === 'note' ? 'note' : 'activity',
    actor: entry.actor,
  }))

  return [...stageItems, ...journalItems]
    .sort((left, right) => timestamp(left.at) - timestamp(right.at))
}

function taskState(status: string, dueDate: string | null, now: Date): JournalTaskRow['state'] {
  if (DONE_STATUSES.has(status.trim().toLowerCase())) return 'done'
  if (!dueDate) return 'open'
  const due = /^\d{4}-\d{2}-\d{2}$/.test(dueDate)
    ? new Date(`${dueDate}T23:59:59.999`).getTime()
    : Date.parse(dueDate)
  return Number.isFinite(due) && due < now.getTime() ? 'overdue' : 'open'
}

export function buildJournalTasks(
  checklists: readonly ClosingChecklist[],
  obligations: readonly ObligationRow[],
  nextActions: readonly NextActionRow[] = [],
  now = new Date(),
): JournalTaskRow[] {
  const checklistRows = checklists.flatMap((checklist) =>
    checklist.categories.flatMap((category) =>
      category.items.map((item) => ({
        id: `checklist-${checklist.id}-${item.id}`,
        checklistId: checklist.id,
        itemId: item.id,
        label: item.label,
        owner: item.responsible,
        dueDate: item.dueDate,
        status: item.status,
        state: taskState(item.status, item.dueDate, now),
        source: 'checklist' as const,
      })),
    ),
  )

  const conditionRows = obligations.map((obligation, index) => ({
    id: `condition-${index}-${obligation.label}`,
    checklistId: null,
    itemId: null,
    label: obligation.label,
    owner: obligation.owner ?? obligation.externalParty,
    dueDate: obligation.due,
    status: obligation.status,
    state: taskState(obligation.status, obligation.due, now),
    source: 'condition' as const,
  }))

  const nextActionRows = nextActions.map((action, index) => ({
    id: `next-action-${index}-${action.action}`,
    checklistId: null,
    itemId: null,
    label: action.action,
    owner: action.owner,
    dueDate: action.due,
    status: 'Open',
    state: taskState('Open', action.due, now),
    source: 'next_action' as const,
  }))

  const seenTasks = new Set<string>()
  return [...checklistRows, ...conditionRows, ...nextActionRows].filter((row) => {
    const key = [row.label, row.owner ?? '', row.dueDate ?? ''].map((value) => value.trim().toLowerCase()).join('|')
    if (seenTasks.has(key)) return false
    seenTasks.add(key)
    return true
  }).sort((left, right) => {
    const rank = { overdue: 0, open: 1, done: 2 }
    const stateDelta = rank[left.state] - rank[right.state]
    if (stateDelta !== 0) return stateDelta
    return timestamp(left.dueDate ?? '') - timestamp(right.dueDate ?? '')
  })
}

export function buildJournalCollaborators(
  dealTeam: readonly DealTeamMember[],
  parties: readonly PartyNode[],
): JournalCollaborator[] {
  const out: JournalCollaborator[] = []
  const seen = new Set<string>()
  const append = (name: string | null | undefined, role: string, relationship: JournalCollaborator['relationship']) => {
    if (!usableName(name)) return
    const key = `${name.trim().toLowerCase()}|${role.trim().toLowerCase()}`
    if (seen.has(key)) return
    seen.add(key)
    out.push({ id: key, name: name.trim(), role, relationship })
  }

  for (const member of dealTeam) append(member.name, member.role, 'internal')
  for (const party of parties) append(party.name, party.role, 'external')
  return out
}
